import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Crop,
  Download,
  BadgeCheck,
  Library,
  Loader2,
  Play,
  Stamp,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { TerminalPanel } from "./TerminalPanel";
import { API_URL, BACKEND_URL } from "./backendUrl";

const API = API_URL;

const ITEM_STATUS = {
  pending: { label: "EN ATTENTE", cls: "bg-slate-100 text-slate-500" },
  cleaning: { label: "NETTOYAGE IA...", cls: "bg-orange-50 text-[#f58207]" },
  cleaned: { label: "PLACER LE LOGO", cls: "bg-amber-50 text-amber-700" },
  placed: { label: "LOGO PLACÉ", cls: "bg-emerald-600 text-white" },
  error: { label: "ERREUR", cls: "bg-red-50 text-red-600" },
};

const prettyDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ZoneEditor = ({ item, logoUrl, placing, onPlace, onClose }) => {
  const wrapRef = useRef(null);
  const [dragStart, setDragStart] = useState(null);
  const [rect, setRect] = useState(item.zone || item.suggested_zone || null);
  const guideGrid = Array.from({ length: 9 }, (_, i) => i);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const getPos = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return {
      x: Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1),
      y: Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1),
    };
  };

  const validRect = rect && rect.w > 0.01 && rect.h > 0.01;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="zone-editor-modal">
      <button
        onClick={onClose}
        data-testid="close-zone-editor-button"
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="flex flex-col items-center gap-4 max-w-[92vw]">
        <p className="text-white/80 text-xs font-mono uppercase tracking-[0.2em] flex items-center gap-2">
          <Crop className="w-4 h-4" /> {item.format_name} · {item.width}×{item.height} — dessinez la zone du logo à la souris
        </p>
        <p className="max-w-2xl text-center text-white/70 text-sm leading-6">
          Utilisation simple: vise un des carrés centraux de la grille pour garder le logo bien centré. Tu peux dessiner
          une zone rapide, la grille t'aide juste à placer proprement.
        </p>
        {item.suggested_zone && !item.zone && (
          <div className="rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white/85">
            Zone détectée automatiquement
          </div>
        )}
        <div
          ref={wrapRef}
          data-testid="zone-editor-image"
          className="relative inline-block select-none cursor-crosshair"
          onMouseDown={(e) => {
            e.preventDefault();
            const p = getPos(e);
            setDragStart(p);
            setRect({ x: p.x, y: p.y, w: 0, h: 0 });
          }}
          onMouseMove={(e) => {
            if (!dragStart) return;
            const p = getPos(e);
            setRect({
              x: Math.min(dragStart.x, p.x),
              y: Math.min(dragStart.y, p.y),
              w: Math.abs(p.x - dragStart.x),
              h: Math.abs(p.y - dragStart.y),
            });
          }}
          onMouseUp={() => setDragStart(null)}
          onMouseLeave={() => setDragStart(null)}
          >
          <img
            src={`${BACKEND_URL}${item.cleaned_url}`}
            alt="fond nettoyé"
            draggable={false}
            className="max-h-[62vh] max-w-[88vw] object-contain rounded-lg pointer-events-none"
          />
          <div className="absolute inset-0 pointer-events-none rounded-lg overflow-hidden">
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
              {guideGrid.map((idx) => (
                <div
                  key={idx}
                  className={`border border-white/18 ${idx === 4 ? "bg-white/10" : "bg-transparent"}`}
                />
              ))}
            </div>
            <div className="absolute inset-0">
              <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/35" />
              <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/35" />
              <div className="absolute top-1/3 left-0 right-0 h-px bg-white/35" />
              <div className="absolute top-2/3 left-0 right-0 h-px bg-white/35" />
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#f58207]/40" />
              <div className="absolute top-1/2 left-0 right-0 h-px bg-[#f58207]/40" />
            </div>
          </div>
          {rect && (
            <div
              data-testid="zone-rect"
              className="absolute border-2 border-[#00FF41] bg-[#00FF41]/10 flex items-center justify-center overflow-hidden"
              style={{
                left: `${rect.x * 100}%`,
                top: `${rect.y * 100}%`,
                width: `${rect.w * 100}%`,
                height: `${rect.h * 100}%`,
              }}
            >
              {logoUrl && validRect && (
                <img src={logoUrl} alt="logo" draggable={false} className="max-w-full max-h-full object-contain opacity-90 pointer-events-none" />
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {item.suggested_zone && (
            <button
              type="button"
              onClick={() => setRect(item.suggested_zone)}
              className="h-11 px-5 bg-white/10 text-white font-bold text-sm rounded-full hover:bg-white/20 transition-colors flex items-center gap-2 border border-white/15"
            >
              Reprendre la zone détectée
            </button>
          )}
          <button
            data-testid="place-logo-button"
            onClick={() => onPlace(rect, false)}
            disabled={!validRect || placing}
            className="h-11 px-5 bg-white text-slate-900 font-bold text-sm rounded-full hover:bg-slate-100 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stamp className="w-4 h-4" />}
            Placer le logo ici
          </button>
          <button
            data-testid="place-logo-all-button"
            onClick={() => onPlace(rect, true)}
            disabled={!validRect || placing}
            className="h-11 px-5 bg-[#f58207] text-white font-bold text-sm rounded-full hover:bg-[#c86b05] transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Appliquer cette zone à toutes les images
          </button>
        </div>
      </div>
    </div>
  );
};

export default function LogoSwapPage({ decl, onBack }) {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoName, setLogoName] = useState("");
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [logos, setLogos] = useState([]);
  const [logosLoading, setLogosLoading] = useState(false);
  const [logosError, setLogosError] = useState("");
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [deletingLogoId, setDeletingLogoId] = useState(null);
  const [swap, setSwap] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [editorIdx, setEditorIdx] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [selectedCleanIndices, setSelectedCleanIndices] = useState(() => new Set(decl.results.map((_, i) => i)));
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  const isRunning = launching || swap?.status === "pending" || swap?.status === "running";
  const libraryCountLabel = `${logos.length} logo${logos.length > 1 ? "s" : ""}`;

  const loadLogos = async () => {
    setLogosLoading(true);
    setLogosError("");
    try {
      const res = await axios.get(`${API}/logos`);
      setLogos(res.data || []);
    } catch (e) {
      setLogosError(e.response?.data?.detail || "Impossible de charger la bibliothèque");
    } finally {
      setLogosLoading(false);
    }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (id) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/logoswap/${id}`);
        setSwap(res.data);
        if (["ready", "partial", "failed"].includes(res.data.status)) {
          stopPolling();
          if (res.data.status === "ready") toast.success("Fonds nettoyés ! Cliquez sur une image pour placer votre logo.");
          else if (res.data.status === "partial") toast.warning("Nettoyage partiel — voir le journal");
          else toast.error("Échec du nettoyage IA");
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  useEffect(() => stopPolling, []);
  useEffect(() => { loadLogos(); }, []);
  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleLogo = (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error("Format non supporté — PNG, JPEG ou WebP");
      return;
    }
    setSelectedLogo(null);
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
    if (!logoName.trim()) {
      setLogoName(f.name.replace(/\.[^.]+$/, ""));
    }
  };

  const selectLogoFromLibrary = (logo) => {
    setLogoFile(null);
    setLogoPreview(null);
    setSelectedLogo(logo);
    setEditorIdx(null);
    if (!swap) {
      toast.success(`Logo sélectionné : ${logo.name}`);
    }
  };

  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setSelectedLogo(null);
  };

  const deleteLibraryLogo = async (logo) => {
    if (!window.confirm(`Supprimer "${logo.name}" de la bibliothèque ?`)) return;
    setDeletingLogoId(logo.id);
    try {
      await axios.delete(`${API}/logos/${logo.id}`);
      if (selectedLogo?.id === logo.id) clearLogo();
      await loadLogos();
      toast.success("Logo supprimé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de supprimer le logo");
    } finally {
      setDeletingLogoId(null);
    }
  };

  const launch = async () => {
    if (!logoFile && !selectedLogo) return toast.error("Choisissez d'abord un logo");
    if (selectedCleanIndices.size === 0) return toast.error("Sélectionnez au moins une image à nettoyer");
    setLaunching(true);
    try {
      const form = new FormData();
      form.append("decl_id", decl.id);
      form.append("selected_indices", Array.from(selectedCleanIndices).sort((a, b) => a - b).join(","));
      if (selectedLogo) {
        form.append("logo_id", selectedLogo.id);
      } else if (logoFile) {
        form.append("logo", logoFile);
        form.append("save_to_library", String(saveToLibrary));
        if (saveToLibrary && logoName.trim()) {
          form.append("logo_name", logoName.trim());
        }
      }
      const res = await axios.post(`${API}/logoswap`, form);
      setSwap(res.data);
      if (logoFile && saveToLibrary) {
        await loadLogos();
      }
      startPolling(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de lancement");
    } finally {
      setLaunching(false);
    }
  };

  const place = async (zone, applyAll) => {
    if (!zone) return;
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/logoswap/${swap.id}/place`, {
        item_idx: editorIdx,
        zone,
        apply_to_all: applyAll,
      });
      setSwap(res.data);
      toast.success(applyAll ? "Logo appliqué à toutes les images !" : "Logo placé !");
      setEditorIdx(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du placement");
    } finally {
      setPlacing(false);
    }
  };

  const downloadZip = async () => {
    if (!swap) return;
    try {
      const res = await fetch(`${API}/logoswap/${swap.id}/zip`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `declinaisons_logo_${swap.id.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const items = swap?.items || decl.results.map((r, i) => ({
    idx: i,
    status: "pending",
    original_url: r.url,
    cleaned_url: null,
    final_url: null,
    zone: null,
    suggested_zone: null,
    format_name: r.format_name,
    width: r.width,
    height: r.height,
    source_index: r.source_index,
    category: r.category,
  }));
  const placedCount = items.filter((it) => it.status === "placed").length;
  const zipReady = swap && ["ready", "partial"].includes(swap.status);
  const editorItem = editorIdx !== null && swap ? swap.items[editorIdx] : null;
  const activeLogoUrl = selectedLogo ? `${BACKEND_URL}${selectedLogo.url}` : logoPreview;
  const launchLabel = selectedLogo
    ? `Lancer le nettoyage IA avec la bibliothèque (${selectedCleanIndices.size} image${selectedCleanIndices.size > 1 ? "s" : ""})`
    : `Lancer le nettoyage IA (${selectedCleanIndices.size} image${selectedCleanIndices.size > 1 ? "s" : ""})`;

  const toggleCleanSelection = (idx) => {
    if (swap || isRunning) return;
    setSelectedCleanIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAllCleanSelection = () => {
    if (swap || isRunning) return;
    setSelectedCleanIndices((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((_, i) => i));
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-body text-slate-700">
      {editorItem && (
        <ZoneEditor
          item={editorItem}
          logoUrl={swap?.logo_url ? `${BACKEND_URL}${swap.logo_url}` : activeLogoUrl}
          placing={placing}
          onPlace={place}
          onClose={() => setEditorIdx(null)}
        />
      )}

      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-to-declinations-button"
            onClick={onBack}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#f58207] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour aux déclinaisons
          </button>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-white border border-slate-200 flex items-center justify-center shadow-sm">
              <img src="/logo-gm.png" alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight text-slate-900 font-heading">Remplacement du logo</h1>
              <p className="text-[11px] text-slate-500 font-mono">Logo remplacé proprement</p>
            </div>
          </div>
        </div>
        {zipReady && (
          <button
            data-testid="logoswap-zip-button"
            onClick={downloadZip}
            className="h-11 px-6 bg-slate-900 text-white font-bold text-sm rounded-lg hover:bg-black transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> ZIP final ({placedCount}/{items.length} logo placé)
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 p-6 flex-1">
        {/* LEFT — logo upload + terminal */}
        <div className="space-y-6 lg:sticky lg:top-24 self-start">
          <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase flex items-center gap-2">
                  <Library className="w-4 h-4" /> 0 · Bibliothèque de logos
                </h2>
                <p className="mt-2 text-xs text-slate-500">
                  Cliquez pour réutiliser un logo déjà enregistré, puis lancez le remplacement sans re-uploader.
                </p>
              </div>
              <button
                type="button"
                onClick={loadLogos}
                className="text-[11px] font-bold text-[#f58207] hover:text-[#c86b05] transition-colors shrink-0"
              >
                Rafraîchir
              </button>
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <span className="flex items-center gap-2">
                <BadgeCheck className="w-3.5 h-3.5 text-[#f58207]" />
                {libraryCountLabel}
              </span>
              <span>Prêt à l'emploi</span>
            </div>
            {logosError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 p-3 text-sm mt-4">
                {logosError}
              </div>
            ) : null}
            <div className="mt-4 space-y-3">
              {logosLoading ? (
                <div className="h-32 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement de la bibliothèque...
                </div>
              ) : logos.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  Aucune image enregistrée pour l'instant. Uploadez un logo ci-dessous, puis cochez l'enregistrement dans la bibliothèque.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1 custom-scrollbar" data-testid="logoswap-library-grid">
                  {logos.map((logo) => {
                    const selected = selectedLogo?.id === logo.id;
                    return (
                      <div
                        key={logo.id}
                        data-testid={`logoswap-library-item-${logo.id}`}
                        className={`group relative rounded-2xl border overflow-hidden transition-all bg-white ${
                          selected ? "border-[#f58207] ring-2 ring-[#f58207]/15 shadow-sm" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => selectLogoFromLibrary(logo)}
                          className="w-full text-left"
                        >
                          <div className="aspect-square bg-[repeating-conic-gradient(#f1f5f9_0%_25%,white_0%_50%)] bg-[length:18px_18px] p-3 flex items-center justify-center">
                            <img
                              src={`${BACKEND_URL}${logo.url}`}
                              alt={logo.name}
                              className="max-w-full max-h-full object-contain transition-transform duration-200 group-hover:scale-[1.02]"
                            />
                          </div>
                          <div className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-900 truncate">{logo.name}</p>
                                <p className="text-[11px] text-slate-500 font-mono mt-1">
                                  {prettyDate(logo.created_at) || "Logo enregistré"}
                                </p>
                              </div>
                              <span
                                className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                                  selected ? "bg-[#f58207] text-white" : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                {selected ? "Actif" : "Utiliser"}
                              </span>
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          data-testid={`logoswap-delete-library-logo-${logo.id}`}
                          onClick={() => deleteLibraryLogo(logo)}
                          disabled={deletingLogoId === logo.id}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-white border border-slate-200 flex items-center justify-center shadow-sm transition-colors disabled:opacity-50"
                          aria-label={`Supprimer ${logo.name}`}
                        >
                          {deletingLogoId === logo.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-700" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5 text-slate-700" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase mb-4 flex items-center gap-2">
              <UploadCloud className="w-4 h-4" /> 1 · Le bon logo
            </h2>
            {activeLogoUrl ? (
              <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="logo-preview">
                <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">
                      Logo actif
                    </p>
                    <h3 className="mt-1 text-sm font-semibold text-slate-900 truncate">
                      {selectedLogo ? selectedLogo.name : logoFile?.name || "Logo importé"}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {selectedLogo ? "Sélectionné depuis la bibliothèque" : "Fichier local prêt pour le swap"}
                    </p>
                  </div>
                  {!isRunning && !swap && (
                    <button
                      data-testid="remove-logo-button"
                      onClick={clearLogo}
                      className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:scale-110 transition-all shadow-sm"
                    >
                      <X className="w-3.5 h-3.5 text-slate-900" />
                    </button>
                  )}
                </div>
                <div className="p-4 bg-[repeating-conic-gradient(#f1f5f9_0%_25%,white_0%_50%)] bg-[length:20px_20px] flex items-center justify-center">
                  <div className="relative rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm w-full flex items-center justify-center min-h-36">
                    <img src={activeLogoUrl} alt="logo" className="max-h-28 max-w-full object-contain" />
                    <div className="absolute bottom-3 left-3 px-2 py-1 rounded-full bg-slate-900/85 text-[10px] font-bold uppercase tracking-wider text-white">
                      {selectedLogo ? "Bibliothèque" : "Upload"}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                data-testid="logo-upload-dropzone"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleLogo(e.dataTransfer.files?.[0]); }}
                className="border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center cursor-pointer flex flex-col items-center gap-3 bg-slate-50 hover:bg-slate-100 transition-colors group"
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="logo-file-input"
                  onChange={(e) => handleLogo(e.target.files?.[0])}
                />
                <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                  <UploadCloud className="w-6 h-6 text-slate-400 group-hover:text-[#f58207] transition-colors" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Glissez le bon logo ici ou cliquez</p>
                  <p className="text-xs text-slate-500 mt-1">PNG transparent recommandé</p>
                </div>
              </div>
            )}

            {logoFile && !selectedLogo && (
              <div className="mt-4 grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <label className="block text-[11px] font-bold tracking-[0.18em] text-slate-500 uppercase mb-2">
                    Nom du logo
                  </label>
                  <input
                    data-testid="logo-name-input"
                    value={logoName}
                    onChange={(e) => setLogoName(e.target.value)}
                    placeholder="Ex: logo principal, monogramme..."
                    className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#f58207]/20 focus:border-[#f58207]"
                  />
                </div>
                <label className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer select-none rounded-xl bg-white border border-slate-200 px-3 py-2">
                  <input
                    data-testid="save-to-library-checkbox"
                    type="checkbox"
                    checked={saveToLibrary}
                    onChange={(e) => setSaveToLibrary(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-[#f58207] focus:ring-[#f58207]"
                  />
                  Enregistrer ce logo dans la bibliothèque
                </label>
              </div>
            )}

            {selectedLogo && (
              <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/70 p-4 text-sm text-orange-900">
                <div className="flex items-center gap-2 font-semibold">
                  <BadgeCheck className="w-4 h-4" />
                  Logo sélectionné depuis la bibliothèque
                </div>
                <p className="mt-1">{selectedLogo.name}</p>
              </div>
            )}

            <button
              data-testid="launch-logoswap-button"
              onClick={launch}
              disabled={isRunning || !!swap || (!logoFile && !selectedLogo) || selectedCleanIndices.size === 0}
              className="mt-4 w-full h-12 bg-[#f58207] text-white font-bold text-sm rounded-xl hover:bg-[#c86b05] transition-colors flex items-center justify-center gap-2 shadow-[0_4px_14px_0_rgba(245,130,7,0.39)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRunning ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Nettoyage IA... {swap ? `${swap.completed || 0}/${swap.total}` : ""}</>
              ) : (
                <><Play className="w-4 h-4" /> {launchLabel}</>
              )}
            </button>
            <p className="text-[11px] text-slate-500 mt-3 italic leading-relaxed">
              Le fond est nettoyé sur chaque déclinaison. Ensuite, cliquez sur une image pour dessiner la zone et y
              coller votre logo. Vous pouvez partir d'un upload ou d'un logo déjà enregistré.
            </p>
          </section>

          <TerminalPanel
            title="Journal — Remplacement du logo"
            testId="logoswap-activity-log"
            badgeTestId="logoswap-status-badge"
            emptyText="$ Uploadez le bon logo puis lancez le nettoyage IA..."
            logs={swap?.logs || []}
            status={swap?.status || (launching ? "pending" : "idle")}
          />
        </div>

        {/* RIGHT — items grid */}
        <section className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase mb-4">
            2 · Images à nettoyer
            <span className="ml-2 text-slate-500 font-mono normal-case font-medium tracking-normal">
              {selectedCleanIndices.size}/{items.length} sélectionnée{selectedCleanIndices.size > 1 ? "s" : ""}
            </span>
          </h2>
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">Décoche les images que tu ne veux pas nettoyer.</p>
            <button
              type="button"
              onClick={toggleAllCleanSelection}
              disabled={isRunning || !!swap}
              className="text-[11px] font-bold text-[#f58207] hover:text-[#c86b05] transition-colors disabled:opacity-40"
            >
              {selectedCleanIndices.size === items.length ? "Tout désélectionner" : "Tout sélectionner"}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="logoswap-items-grid">
            {items.map((it) => {
              const st = ITEM_STATUS[it.status] || ITEM_STATUS.pending;
              const url = it.final_url || it.cleaned_url || it.original_url;
              const clickable = swap && (it.status === "cleaned" || it.status === "placed");
              const selectedForCleaning = !swap && selectedCleanIndices.has(it.idx);
              return (
                <button
                  key={it.idx}
                  data-testid={`logoswap-item-${it.idx}`}
                  onClick={() => (swap ? (clickable && setEditorIdx(it.idx)) : toggleCleanSelection(it.idx))}
                  disabled={isRunning || (swap && !clickable)}
                  className={`rounded-lg overflow-hidden border text-left transition-all ${
                    swap
                      ? clickable
                        ? "border-slate-200 hover:border-[#f58207] hover:shadow-md cursor-pointer"
                        : "border-slate-200 opacity-80 cursor-default"
                      : selectedForCleaning
                      ? "border-[#f58207] ring-2 ring-[#f58207]/20 shadow-sm cursor-pointer"
                      : "border-slate-200 opacity-70 hover:opacity-100 hover:border-[#f58207]/50 cursor-pointer"
                  }`}
                >
                  <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden relative">
                    <img src={`${BACKEND_URL}${url}`} alt={it.format_name} className="max-w-full max-h-full object-contain" />
                    {!swap && (
                      <div
                        className={`absolute top-2 right-2 w-6 h-6 rounded-full border flex items-center justify-center ${
                          selectedForCleaning ? "bg-[#f58207] border-[#f58207] text-white" : "bg-white/90 border-slate-200 text-slate-400"
                        }`}
                      >
                        {selectedForCleaning ? <Check className="w-3.5 h-3.5 text-white stroke-[4]" /> : <span className="w-2 h-2 rounded-full bg-current" />}
                      </div>
                    )}
                    {it.status === "cleaning" && (
                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-[#f58207]" />
                      </div>
                    )}
                  </div>
                  <div className="p-2 bg-white">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider ${st.cls}`}>
                      {st.label}
                    </span>
                    {it.suggested_zone && !it.zone && (
                      <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-orange-50 text-[#f58207]">
                        zone détectée
                      </span>
                    )}
                    <p className="text-[11px] font-mono text-slate-900 truncate mt-1">
                      {it.format_name} · {it.width}×{it.height}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono">
                      {it.category.replace(/_/g, " ")} · #{it.source_index}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
